import { Avatar, DATE_FORMAT, GuestStatus, IGuestListItem, ISort, ListItem, Pill, StringUtils } from '@localina/core';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SortBy } from '../../enums';

interface IProps {
    guest: IGuestListItem;
    sort: ISort;

    selected?: boolean;
    onClick: (guest: IGuestListItem) => void;
}

export const GuestListItem: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const { guest, numReservations, lastReservationDate: reservationDate } = props.guest;
    const { firstName, lastName, email, guestStatus, phoneNumbers } = guest;
    const titleText = StringUtils.combineStrings(
        props.sort.by === SortBy.FIRST_NAME_LAST_NAME ? [firstName, lastName] : [lastName, firstName],
        ' ',
    );

    const lastReservationDate = reservationDate ? DateTime.fromISO(reservationDate).toFormat(DATE_FORMAT) : null;
    const phoneNumber = phoneNumbers?.[0];

    const title = (
        <div className="guest-listitem__title">
            <span className="guest-listitem__name">{titleText}</span>
            {}
            {guestStatus === GuestStatus.VIP && (
                <Pill tone="warn" label={t('guest.details.view.fields.guestStatus.vip')} />
            )}
            {guestStatus === GuestStatus.BLACKLIST && (
                <Pill tone="critical" label={t('guest.details.view.fields.guestStatus.blacklist')} />
            )}
        </div>
    );

    const subtitle = (
        <div className="guest-listitem__subtitle">
            {}
            <span className="guest-listitem__last">
                {numReservations > 0 && lastReservationDate
                    ? t('guests.list.lastReservation', { date: lastReservationDate })
                    : t('guests.list.noReservations')}
            </span>
        </div>
    );

    // Compact rows show the bare date in the corner, as a mail list does. The labelled line above
    // is then only visually hidden, so screen readers still hear "Last reservation …".
    const date = numReservations > 0 && lastReservationDate && (
        <span className="guest-listitem__date" aria-hidden={true}>
            {lastReservationDate}
        </span>
    );

    const meta = (
        <div className="guest-listitem__meta">
            {numReservations > 0 && (
                <Pill
                    tone={numReservations > 1 ? 'teal' : 'neutral'}
                    label={t('guests.list.visits', { count: numReservations })}
                />
            )}
            {}
            <div className="guest-listitem__contact">
                {Boolean(phoneNumber) && (
                    <span className="guest-listitem__phone">{StringUtils.formatPhoneNumber(phoneNumber)}</span>
                )}
                {Boolean(email) && <span className="guest-listitem__email">{email}</span>}
            </div>
        </div>
    );

    const handleClick = () => {
        props.onClick(props.guest);
    };

    return (
        <ListItem
            classNames={StringUtils.combineStrings(['guest-listitem', props.selected && 'is-selected'])}
            title={
                <div className="guest-listitem__row">
                    <div className="guest-listitem__main">
                        {title}
                        {subtitle}
                    </div>
                    {date}
                    {meta}
                </div>
            }
            onClick={handleClick}
            leftIcon={<Avatar name={titleText} />}
        />
    );
};
